import { AuthError, type Session } from '@supabase/supabase-js';

import { getSupabaseClient } from '@/lib/supabase/client';
import { invokeFunction } from '@/lib/supabase/functions';

import type { Profile } from './types';

export class AuthRequestError extends Error {
  constructor(
    message: string,
    readonly kind: 'invalid_credentials' | 'network' | 'rate_limited' | 'unknown',
  ) {
    super(message);
    this.name = 'AuthRequestError';
  }
}

/** Maps Supabase Auth errors to actionable Spanish messages without leaking details. */
export function toAuthRequestError(error: unknown): AuthRequestError {
  if (error instanceof AuthError) {
    if (error.status === 400 || error.code === 'invalid_credentials') {
      return new AuthRequestError('Correo o contraseña incorrectos.', 'invalid_credentials');
    }
    if (error.status === 429) {
      return new AuthRequestError(
        'Demasiados intentos. Espera unos minutos antes de volver a intentarlo.',
        'rate_limited',
      );
    }
    if (error.name === 'AuthRetryableFetchError' || error.status === 0) {
      return new AuthRequestError(
        'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.',
        'network',
      );
    }
  }
  if (error instanceof TypeError) {
    return new AuthRequestError(
      'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.',
      'network',
    );
  }
  return new AuthRequestError(
    'No se pudo completar la operación. Inténtalo nuevamente.',
    'unknown',
  );
}

export async function getCurrentSession(): Promise<Session | null> {
  const { data, error } = await getSupabaseClient().auth.getSession();
  if (error) throw toAuthRequestError(error);
  return data.session;
}

export function onAuthSessionChange(
  listener: (event: string, session: Session | null) => void,
): () => void {
  const { data } = getSupabaseClient().auth.onAuthStateChange(listener);
  return () => {
    data.subscription.unsubscribe();
  };
}

export async function signInWithPassword(email: string, password: string): Promise<void> {
  const { error } = await getSupabaseClient().auth.signInWithPassword({ email, password });
  if (error) throw toAuthRequestError(error);
}

export async function signOut(): Promise<void> {
  // Local scope: clears this browser session even if the network call fails.
  const { error } = await getSupabaseClient().auth.signOut({ scope: 'local' });
  if (error) throw toAuthRequestError(error);
}

export async function requestPasswordReset(email: string, redirectTo: string): Promise<void> {
  const { error } = await getSupabaseClient().auth.resetPasswordForEmail(email, { redirectTo });
  // Never reveal whether the email exists: only network/rate errors are surfaced.
  if (error && error.status !== 400 && error.status !== 404) throw toAuthRequestError(error);
}

export async function updatePassword(password: string): Promise<void> {
  const { error } = await getSupabaseClient().auth.updateUser({ password });
  if (error) throw toAuthRequestError(error);
}

export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await getSupabaseClient()
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function updateOwnName(userId: string, fullName: string): Promise<void> {
  const { error } = await getSupabaseClient()
    .from('profiles')
    .update({ full_name: fullName })
    .eq('id', userId);
  if (error) throw error;
}

// -----------------------------------------------------------------------------
// Invitation onboarding (/aceptar-invitacion)
// -----------------------------------------------------------------------------

/** The link is unusable (expired, already used, cancelled or tampered). */
export class InvitationLinkError extends Error {
  constructor(readonly kind: 'invalid' | 'network') {
    super(
      kind === 'network'
        ? 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo nuevamente.'
        : 'Esta invitación ya no es válida.',
    );
    this.name = 'InvitationLinkError';
  }
}

function toInvitationLinkError(error: unknown): InvitationLinkError {
  return new InvitationLinkError(
    toAuthRequestError(error).kind === 'network' ? 'network' : 'invalid',
  );
}

/** Exchanges the invitation email's token_hash for a session (verifyOtp). */
export async function verifyInvitationToken(tokenHash: string): Promise<void> {
  const { error } = await getSupabaseClient().auth.verifyOtp({
    token_hash: tokenHash,
    type: 'invite',
  });
  if (error) throw toInvitationLinkError(error);
}

/**
 * Fallback for Supabase's default invite template, which returns implicit
 * tokens in the URL hash (our PKCE client does not consume them by itself).
 */
export async function setSessionFromInviteLink(
  accessToken: string,
  refreshToken: string,
): Promise<void> {
  const { error } = await getSupabaseClient().auth.setSession({
    access_token: accessToken,
    refresh_token: refreshToken,
  });
  if (error) throw toInvitationLinkError(error);
}

export interface OwnInvitation {
  email: string;
  full_name: string;
  role: Profile['role'];
  accepted_at: string | null;
  cancelled_at: string | null;
}

/** The signed-in user's latest invitation (RLS: own row only). */
export async function fetchOwnInvitation(userId: string): Promise<OwnInvitation | null> {
  const { data, error } = await getSupabaseClient()
    .from('user_invitations')
    .select('email, full_name, role, accepted_at, cancelled_at')
    .eq('auth_user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * Sets the password and activates the account server-side (user-admin Edge
 * Function). The role is never sent: it was fixed by the SUPERADMIN.
 * Setting the password ends the invitation-link session; sign in afterwards.
 */
export async function completeInvitation(input: {
  password: string;
  fullName: string;
}): Promise<{ role: Profile['role'] }> {
  return invokeFunction<{ role: Profile['role'] }>('user-admin', {
    action: 'accept_invitation',
    password: input.password,
    fullName: input.fullName,
  });
}
